// forms/CompanySetupForm.tsx
import React, { useState, useEffect, useRef } from 'react';
import { BaseFormProps, CompanySetupFormData } from '../../../types/FormTypes';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { 
  faBuilding, 
  faBullseye,
} from '@fortawesome/free-solid-svg-icons';
import { 
  Handshake, 
  Target, 
  Home, 
  Shield, 
  PoundSterling,
  CheckCircle, 
  AlertTriangle,
  ChevronDown,
  User,
  Loader2,
  Edit
} from 'lucide-react';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/semantic-ui.css';
import { fetchUserProfile } from '../../../utils/user';
import useTokenStore from '../../../store/store';

interface CompanySetupFormProps extends BaseFormProps<CompanySetupFormData> {}

// Accordion Item Component
interface AccordionItemProps {
  title: string;
  content: string;
  defaultOpen?: boolean;
}

const AccordionItem: React.FC<AccordionItemProps> = ({ title, content, defaultOpen = false }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div className="border border-gray-200 rounded-lg">
      <button
        type="button"
        className="w-full px-4 py-3 text-left flex items-center justify-between hover:bg-gray-50 transition-colors"
        onClick={() => setIsOpen(!isOpen)}
      >
        <h6 className="font-semibold text-sm">{title}</h6>
        <ChevronDown
          className={`w-5 h-5 text-gray-500 transition-transform ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>
      {isOpen && (
        <div className="px-4 pb-3">
          <p className="text-sm text-gray-600">{content}</p>
        </div>
      )}
    </div>
  );
};

const CompanySetupForm: React.FC<CompanySetupFormProps> = ({
  initialData,
  errors = {},
  isSubmitting = false,
  onSubmit,
  onCancel,
  themes
}) => {
  // Default form structure
  const getDefaultData = (): CompanySetupFormData => ({
    companyName: '',
    companyType: 'standalone',
    sicCode: '',
    registeredAddress: {
      country: '',
      flatNumber: '',
      line1: '',
      line2: '',
      city: '',
      postcode: ''
    },
    personalDetails: {
      email: '',
      firstName: '',
      middleName: '',
      lastName: '',
      phone: '',
      changedName: false,
      preferredDifferentName: false,
      previousNames: '',
      preferredName: '',
      dateOfBirth: {
        day: '',
        month: '',
        year: ''
      },
      nationality: '',
      cityOfBirth: '',
      passportNumber: '',
      employmentStatus: '',
      employmentStatusOther: '',
      profession: '',
      isFirstTimeLandlord: '',
      isFirstTimeBuyer: '',
      globalPropertiesOwned: '',
      ukPropertiesOwned: ''
    },
    serviceLevel: 'basic',
    additionalServices: []
  });

  // Local form state - stateless approach
  const [formData, setFormData] = useState<CompanySetupFormData>(() => ({
    ...getDefaultData(),
    ...initialData
  }));
  
  const [currentStep, setCurrentStep] = useState(1);
  const [localErrors, setLocalErrors] = useState<Record<string, string>>({});
  const hasLoadedProfile = useRef(false);
  const getToken = useTokenStore((state) => state.token);

  // Sync with parent data changes and fetch user profile
  useEffect(() => {
    if (initialData) {
      setFormData((prev: CompanySetupFormData) => ({ ...prev, ...initialData }));
    }
  }, [initialData]);

  // Fetch and auto-populate user profile data
 // Replace the useEffect in CompanySetupForm.tsx that handles user profile loading
useEffect(() => {
  console.log('🔄 CompanySetupForm useEffect triggered');
  console.log('📊 Current form data:', formData.personalDetails);
  console.log('🔍 hasLoadedProfile.current:', hasLoadedProfile.current);
  console.log('🔑 User token exists:', !!getToken);
  
  const loadUserProfile = async () => {
    console.log('🚀 Starting loadUserProfile...');
    try {
      // Pass the token directly to fetchUserProfile (same pattern as favorites)
      const userProfile = await fetchUserProfile(getToken || '');
      if (userProfile) {
        console.log('✅ Auto-populating form with user profile:', userProfile);
        setFormData((prev: CompanySetupFormData) => {
          const updatedData = {
            ...prev,
            personalDetails: {
              ...prev.personalDetails,
              firstName: userProfile.firstName || prev.personalDetails.firstName,
              lastName: userProfile.lastName || prev.personalDetails.lastName,
              email: userProfile.email || prev.personalDetails.email,
              phone: userProfile.phone || prev.personalDetails.phone,
            }
          };
          console.log('📝 Updated form data:', updatedData.personalDetails);
          return updatedData;
        });
      } else {
        console.log('❌ No user profile returned from fetchUserProfile');
      }
    } catch (error) {
      console.error('❌ Failed to load user profile:', error);
    }
  };

  // Only fetch if user is authenticated, haven't already loaded the profile, and don't have user data
  const hasUserData = formData.personalDetails.firstName || 
                     formData.personalDetails.lastName || 
                     formData.personalDetails.email || 
                     formData.personalDetails.phone;
  
  console.log('🔍 hasUserData:', hasUserData);
  console.log('🔍 hasLoadedProfile.current:', hasLoadedProfile.current);
  console.log('🔑 User is authenticated:', !!getToken);
  
  if (getToken && !hasLoadedProfile.current && !hasUserData) {
    console.log('🚀 Loading user profile...');
    hasLoadedProfile.current = true;
    loadUserProfile();
  } else {
    console.log('⏭️ Skipping user profile load - not authenticated, already loaded, or has data');
  }
}, [getToken, formData.personalDetails]); // Add formData.personalDetails to dependencies

  // Validation
  const validateStep = (step: number): boolean => {
    const newErrors: Record<string, string> = {};
    
    switch (step) {
      case 1:
        if (!formData.companyType || (formData.companyType !== 'standalone' && formData.companyType !== 'holding')) {
          newErrors.companyType = 'Please select a company structure';
        }
        break;
      case 2:
        const { email, firstName, lastName, phone, changedName, preferredDifferentName, previousNames, preferredName } = formData.personalDetails;
        if (!email.trim() || !email.includes('@')) newErrors['personal.email'] = 'Valid email is required';
        if (!firstName.trim()) newErrors['personal.firstName'] = 'First name is required';
        if (!lastName.trim()) newErrors['personal.lastName'] = 'Surname is required';
        if (!phone.trim() || phone.length < 10) newErrors['personal.phone'] = 'Valid phone number is required';
        
        // Validate conditional fields
        if (changedName && !previousNames.trim()) {
          newErrors['personal.previousNames'] = 'Previous name(s) is required when you have changed your name';
        }
        if (preferredDifferentName && !preferredName.trim()) {
          newErrors['personal.preferredName'] = 'Preferred name is required when you prefer to be called a different name';
        }
        break;
      case 3:
        const { dateOfBirth, nationality, cityOfBirth, passportNumber, employmentStatus, employmentStatusOther, profession, isFirstTimeLandlord, isFirstTimeBuyer } = formData.personalDetails;
        
        // Validate date of birth
        if (!dateOfBirth.day || !dateOfBirth.month || !dateOfBirth.year) {
          newErrors['personal.dateOfBirth'] = 'Date of birth is required';
        }
        
        // Validate nationality
        if (!nationality.trim()) newErrors['personal.nationality'] = 'Nationality is required';
        
        // Validate city of birth
        if (!cityOfBirth.trim()) newErrors['personal.cityOfBirth'] = 'City of birth is required';
        
        // Validate passport number
        if (!passportNumber.trim()) newErrors['personal.passportNumber'] = 'Passport number is required';
        
        // Validate employment status
        if (!employmentStatus.trim()) newErrors['personal.employmentStatus'] = 'Employment status is required';
        
        // Validate employment status other
        if (employmentStatus === 'Other' && !employmentStatusOther.trim()) {
          newErrors['personal.employmentStatusOther'] = 'Please explain your employment status';
        }
        
        // Validate profession
        if (!profession.trim()) newErrors['personal.profession'] = 'Profession is required';
        
        // Validate first time landlord
        if (!isFirstTimeLandlord.trim()) newErrors['personal.isFirstTimeLandlord'] = 'Please select if you are a first time landlord';
        
        // Validate first time buyer (only if first time landlord is yes)
        if (isFirstTimeLandlord === 'yes' && !isFirstTimeBuyer.trim()) {
          newErrors['personal.isFirstTimeBuyer'] = 'Please select if you are a first time buyer';
        }
        
        // Validate property ownership (only if first time landlord is no)
        if (isFirstTimeLandlord === 'no') {
          if (!formData.personalDetails.globalPropertiesOwned.trim()) {
            newErrors['personal.globalPropertiesOwned'] = 'Please specify how many buy-to-let properties you own globally';
          }
          if (!formData.personalDetails.ukPropertiesOwned.trim()) {
            newErrors['personal.ukPropertiesOwned'] = 'Please specify how many of these are in the UK';
          }
          // Validate that UK properties cannot exceed global properties
          const globalCount = parseInt(formData.personalDetails.globalPropertiesOwned) || 0;
          const ukCount = parseInt(formData.personalDetails.ukPropertiesOwned) || 0;
          if (ukCount > globalCount) {
            newErrors['personal.ukPropertiesOwned'] = 'UK properties cannot exceed total global properties';
          }
        }
        break;
      case 4:
        if (!formData.registeredAddress.country.trim()) newErrors['address.country'] = 'Country is required';
        if (!formData.registeredAddress.flatNumber.trim()) newErrors['address.flatNumber'] = 'Flat number / Building number / House name is required';
        if (!formData.registeredAddress.line1.trim()) newErrors['address.line1'] = 'Address line 1 is required';
        if (!formData.registeredAddress.city.trim()) newErrors['address.city'] = 'City is required';
        if (!formData.registeredAddress.postcode.trim()) newErrors['address.postcode'] = 'Postcode is required';
        break;
      case 5:
        // Review step - no validation needed
        break;
    }
    
    setLocalErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Form field updater
  const updateField = (path: string, value: any) => {
    setFormData((prev: CompanySetupFormData) => {
      const keys = path.split('.');
      const newData = { ...prev };
      let current: any = newData;
      
      for (let i = 0; i < keys.length - 1; i++) {
        const key = keys[i];
        if (key.includes('[') && key.includes(']')) {
          const [arrayKey, indexStr] = key.split('[');
          const index = parseInt(indexStr.replace(']', ''));
          if (!current[arrayKey]) current[arrayKey] = [];
          if (!current[arrayKey][index]) current[arrayKey][index] = {};
          current = current[arrayKey][index];
        } else {
          if (!current[key]) current[key] = {};
          current = current[key];
        }
      }
      
      const lastKey = keys[keys.length - 1];
      current[lastKey] = value;
      
      return newData;
    });
  };

  // Navigation
  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, 5));
      // Scroll to top of the form container
      const formContainer = document.querySelector('.flex-1.overflow-y-auto');
      if (formContainer) {
        formContainer.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }
  };

  const handleBack = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
    // Scroll to top of the form container
    const formContainer = document.querySelector('.flex-1.overflow-y-auto');
    if (formContainer) {
      formContainer.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // Submit with payment calculation
  const handleSubmit = () => {
    if (validateStep(currentStep)) {
      const requiresPayment = formData.serviceLevel !== 'basic' || formData.additionalServices.length > 0;
      onSubmit(formData, requiresPayment);
    }
  };

  // Combine local and parent errors
  const allErrors = { ...localErrors, ...errors };

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <div className="flex justify-center mb-4">
                <Handshake className="w-16 h-16 text-blue-600" />
              </div>
              <h3 className="text-2xl font-bold mb-2">Choose your company structure</h3>
              <p className="text-gray-600">Select the most suitable company structure for your property investment</p>
            </div>
            
            {/* Company Structure Options - Side by Side Version */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6 max-w-4xl mx-auto">
                <button
                  type="button"
                  className={`border-2 p-4 rounded-lg transition-all duration-200 text-left w-full ${
                    formData.companyType === 'standalone' 
                      ? 'shadow-md' 
                      : 'border-gray-200 hover:border-gray-300 hover:shadow-sm'
                  }`}
                  style={{
                    borderColor: formData.companyType === 'standalone' ? (themes?.color1 || '#1E5188') : undefined,
                    backgroundColor: formData.companyType === 'standalone' ? 
                      `${themes?.color1 || '#1E5188'}15` : undefined
                  }}
                  onClick={() => !isSubmitting && updateField('companyType', 'standalone')}
                  disabled={isSubmitting}
                >
                  <div className="flex items-start gap-3">
                    <div 
                      className={`w-4 h-4 rounded-full border-2 flex-shrink-0 mt-1 ${
                        formData.companyType === 'standalone' 
                          ? 'border-current' 
                          : 'border-gray-300'
                      }`}
                      style={{
                        backgroundColor: formData.companyType === 'standalone' ? (themes?.color1 || '#1E5188') : 'transparent',
                        borderColor: formData.companyType === 'standalone' ? (themes?.color1 || '#1E5188') : undefined
                      }}
                    >
                      {formData.companyType === 'standalone' && (
                        <div 
                          className="w-2 h-2 rounded-full m-0.5"
                          style={{ backgroundColor: themes?.fontColor || '#ffffff' }}
                        ></div>
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <FontAwesomeIcon icon={faBuilding} className="text-lg text-blue-600 flex-shrink-0" />
                        <h4 className="font-semibold text-base">Standalone Company</h4>
                      </div>
                      <p className="text-sm text-gray-600 leading-relaxed">
                        Simple single company structure - perfect for landlords with 1-2 properties
                      </p>
                    </div>
                  </div>
                </button>

                <button
                  type="button"
                  className={`border-2 p-4 rounded-lg transition-all duration-200 text-left w-full ${
                    formData.companyType === 'holding' 
                      ? 'shadow-md' 
                      : 'border-gray-200 hover:border-gray-300 hover:shadow-sm'
                  }`}
                  style={{
                    borderColor: formData.companyType === 'holding' ? (themes?.color1 || '#1E5188') : undefined,
                    backgroundColor: formData.companyType === 'holding' ? 
                      `${themes?.color1 || '#1E5188'}15` : undefined
                  }}
                  onClick={() => !isSubmitting && updateField('companyType', 'holding')}
                  disabled={isSubmitting}
                >
                  <div className="flex items-start gap-3">
                    <div 
                      className={`w-4 h-4 rounded-full border-2 flex-shrink-0 mt-1 ${
                        formData.companyType === 'holding' 
                          ? 'border-current' 
                          : 'border-gray-300'
                      }`}
                      style={{
                        backgroundColor: formData.companyType === 'holding' ? (themes?.color1 || '#1E5188') : 'transparent',
                        borderColor: formData.companyType === 'holding' ? (themes?.color1 || '#1E5188') : undefined
                      }}
                    >
                      {formData.companyType === 'holding' && (
                        <div 
                          className="w-2 h-2 rounded-full m-0.5"
                          style={{ backgroundColor: themes?.fontColor || '#ffffff' }}
                        ></div>
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <FontAwesomeIcon icon={faBullseye} className="text-lg text-blue-600 flex-shrink-0" />
                        <h4 className="font-semibold text-base">Holding & Subsidiary</h4>
                      </div>
                      <p className="text-sm text-gray-600 leading-relaxed">
                        Parent company with subsidiaries - ideal for larger property portfolios
                      </p>
                    </div>
                  </div>
                </button>
              </div>

            {/* Dynamic content based on selection */}
            <div className="bg-gray-50 p-6 rounded-xl">
              {formData.companyType === 'standalone' ? (
                <>
                  <h4 className="font-bold text-lg mb-4 text-center">Why a standalone company?</h4>
                  <div className="grid md:grid-cols-3 gap-6 text-sm">
                    <div className="text-center">
                      <Home className="w-8 h-8 mx-auto mb-3 text-blue-600" />
                      <h5 className="font-semibold mb-2">Keep it simple</h5>
                      <p className="text-gray-600">A standalone company is perfect for landlords with 1-2 properties who want to keep everything in one place</p>
                    </div>
                    <div className="text-center">
                      <PoundSterling className="w-8 h-8 mx-auto mb-3 text-green-600" />
                      <h5 className="font-semibold mb-2">Stay tax efficient</h5>
                      <p className="text-gray-600">Benefit from all the tax efficiencies that come with a limited company</p>
                    </div>
                    <div className="text-center">
                      <Shield className="w-8 h-8 mx-auto mb-3 text-purple-600" />
                      <h5 className="font-semibold mb-2">Limit personal liability</h5>
                      <p className="text-gray-600">A limited company creates an added layer of legal protection between you and your property investment</p>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <h4 className="font-bold text-lg mb-4 text-center">Why a holding company and subsidiaries?</h4>
                  <div className="grid md:grid-cols-3 gap-6 text-sm">
                    <div className="text-center">
                      <Handshake className="w-8 h-8 mx-auto mb-3 text-blue-600" />
                      <h5 className="font-semibold mb-2">All in one place</h5>
                      <p className="text-gray-600">Ideal for larger portfolios, letting you manage all your properties under one roof while keeping things organised.</p>
                    </div>
                    <div className="text-center">
                      <Target className="w-8 h-8 mx-auto mb-3 text-green-600" />
                      <h5 className="font-semibold mb-2">Subsidiaries for each property</h5>
                      <p className="text-gray-600">Keep your portfolio tidy with a subsidiary for each company</p>
                    </div>
                    <div className="text-center">
                      <CheckCircle className="w-8 h-8 mx-auto mb-3 text-purple-600" />
                      <h5 className="font-semibold mb-2">All the benefits of a standalone</h5>
                      <p className="text-gray-600">Get all the benefits of a standalone company but with a structure suited to larger portfolios</p>
                    </div>
                  </div>
                </>
              )}
            </div>

            {/* Pricing */}
            <div className="bg-gradient-to-r from-blue-50 to-green-50 p-6 rounded-xl border border-blue-200">
              <div className="mb-4">
              <div className="flex justify-between gap-4 items-center">
                <div className="flex flex-col items-start">
                  <h4 className="font-bold text-lg">
                    {formData.companyType === 'holding' ? 'Holding Company Setup' : 'Company Setup'}
                  </h4>
                  <p className="text-sm text-gray-600">To pay today</p>
                </div>
                <div className="text-right">
                  <p className="text-3xl font-bold text-blue-600">£149.00</p>
                </div>
              </div>

              </div>
              
              <div>
              <div className="flex justify-between gap-4 items-center mt-4">
                <div className="flex flex-col items-start">
                  <h4 className="font-bold text-lg">
                    {formData.companyType === 'holding' ? 'Holding Company Management' : 'Company Management'}
                  </h4>
                  <p className="text-sm text-gray-600">Paid monthly</p>
                </div>
                <div className="text-right">
                  <p className="text-3xl font-bold text-green-600">£29.00</p>
                  {formData.companyType === 'holding' && (
                    <p className="text-sm text-gray-600 mt-1">+£29.00 per subsidiary</p>
                  )}
                </div>
              </div>
              </div>

              <div className="mt-4 pt-4 border-t border-blue-200">
                <p className="text-sm text-gray-600 text-center">
                  All prices are based at your current location in the United Kingdom and exclude VAT.
                </p>
                <div className="flex items-center justify-center gap-2 mt-2">
                  <CheckCircle className="w-4 h-4 text-green-600" />
                  <p className="text-sm text-green-700 font-medium">
                    Setup is free for your third company and for all additional companies after that.
                  </p>
                </div>
              </div>
            </div>

            {/* What's included accordion */}
            <div className="bg-white border border-gray-200 rounded-xl p-6">
              <h4 className="font-bold text-lg mb-4 text-center">What's included?</h4>
              
              {formData.companyType === 'standalone' ? (
                // Standard (Standalone) accordion items
                <div className="space-y-2">
                  <AccordionItem 
                    title="Company Formation"
                    content="A purpose-built UK limited company for property investments"
                  />
                  <AccordionItem 
                    title="Company Structuring"
                    content="Company structure optimised for tax-efficiency for you and all your shareholders"
                  />
                  <AccordionItem 
                    title="Legal Documents"
                    content="Purpose-built legal documents to protect your investment, set up director's loan and share transfers"
                  />
                  <AccordionItem 
                    title="Registered Office"
                    content="UK registered office address"
                  />
                  <AccordionItem 
                    title="Financial Accounts"
                    content="A dedicated UK Financial Account for your to help manage your finances, track cash flow, and streamline payments"
                  />
                  <AccordionItem 
                    title="Accounting and Tax"
                    content="Annual accounts preparation and filing, Corporation Tax returns, and full company secretarial inc Confirmation statement & ATED filing"
                  />
                  <AccordionItem 
                    title="Post Management"
                    content="All company mail uploaded to your dashboard securely and digitally"
                  />
                  <AccordionItem 
                    title="Expert Support"
                    content="Access to our expert support and strategy team for any questions throughout your journey"
                  />
                </div>
              ) : (
                // Holding & Subsidiary accordion items
                <div className="space-y-2">
                  <AccordionItem 
                    title="Company Formation"
                    content="A purpose-built UK holding company that can own multiple subsidiary companies, allowing you to manage multiple properties under one umbrella"
                  />
                  <AccordionItem 
                    title="Subsidiaries for Each Property"
                    content="Each property can be held in its own subsidiary, optimising management and financial advantages"
                  />
                  <AccordionItem 
                    title="Company Structuring"
                    content="We support you in establishing the best structure for your holding company, which will help you make the most of your property investments financially"
                  />
                  <AccordionItem 
                    title="Legal Documents"
                    content="Like standalone companies, we prepare all the necessary legal documents for your holding company and its subsidiaries, ensuring all requirements are met"
                  />
                  <AccordionItem 
                    title="Registered Office"
                    content="Your holding company will have a dedicated registered office. This keeps all your official communications separate and secure"
                  />
                  <AccordionItem 
                    title="Financial Account"
                    content="A Financial Account will be set up for your holding company. This account manages transactions for all subsidiary properties efficiently"
                  />
                  <AccordionItem 
                    title="Accounting and Tax"
                    content="Our accounting services cover all subsidiaries, ensuring compliance for your holding company and assisting with tax efficiency"
                  />
                  <AccordionItem 
                    title="Post Management"
                    content="Post management services apply here as well, with all mail scanned, uploaded, and accessible online for your holding company and its subsidiaries"
                  />
                </div>
              )}
            </div>
            
                          {allErrors.companyType && (
                <p className="text-red-500 text-sm mt-2 text-center flex items-center justify-center gap-2">
                  <AlertTriangle className="w-4 h-4" />
                  {allErrors.companyType}
                </p>
              )}
          </div>
        );

        case 2:
          return (
            <div className="space-y-6">
              <div className="text-center mb-6">
                <h3 className="text-xl font-bold">Your Personal Details</h3>
                <p className="text-gray-600 mt-2">We need this information to register the company.</p>
              </div>
        
              {/* Email */}
              <div>
                <label className="block text-sm font-medium mb-1">Email *</label>
                                <input
                  type="email"
                  value={formData.personalDetails.email}
                  onChange={(e) => updateField('personalDetails.email', e.target.value)}
                  className={`w-full p-3 border-2 rounded-lg transition-colors ${
                    allErrors['personal.email'] ? 'border-red-500 bg-red-50' : 'border-gray-300 focus:border-blue-500'
                  }`}
                  placeholder="you@example.com"
                />
                {allErrors['personal.email'] && (
                  <p className="text-red-500 text-sm mt-1 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4" />
                    {allErrors['personal.email']}
                  </p>
                )}
                <p className="text-xs text-gray-500 mt-2">
                  By clicking next, I accept the{' '}
                  <button
                    type="button"
                    onClick={() => window.open('/privacy-policy', '_blank')}
                    className="text-blue-600 hover:text-blue-800 underline"
                  >
                    Terms and Conditions
                  </button>
                  {' '}and{' '}
                  <button
                    type="button"
                    onClick={() => window.open('/privacy-policy', '_blank')}
                    className="text-blue-600 hover:text-blue-800 underline"
                  >
                    Privacy Policy
                  </button>
                  .
                </p>
              </div>

              {/* Phone Number */}
              <div>
                <label className="block text-sm font-medium mb-1">
                  Phone number *
                </label>
                <p className="text-xs text-gray-500 mb-2">
                  This will be used to verify your account when you log in and make payments.
                </p>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    border: allErrors['personal.phone'] ? '2px solid #ef4444' : '2px solid #d1d5db',
                    borderRadius: '0.5rem',
                    backgroundColor: allErrors['personal.phone'] ? '#fef2f2' : '#FFF',
                    padding: '0.25rem',
                    position: 'relative',
                  }}
                >
                  <PhoneInput
                    country={'gb'}
                    value={formData.personalDetails.phone}
                    onChange={(phone: string) => updateField('personalDetails.phone', phone)}
                    enableSearch={true}
                    inputStyle={{
                      border: 'none',
                      outline: 'none',
                      width: '100%',
                      fontSize: '1rem',
                      color: '#2D3748',
                      paddingLeft: '48px',
                      backgroundColor: 'transparent',
                    }}
                    inputProps={{
                      name: 'phone',
                      required: true,
                      placeholder: 'No leading 0 after country code',
                    }}
                    buttonStyle={{
                      backgroundColor: 'transparent',
                      border: 'none',
                      position: 'absolute',
                      left: '4px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'flex-start',
                      width: '24px',
                      height: '24px',
                    }}
                    dropdownStyle={{
                      position: 'absolute',
                      top: 'calc(100% + 20px)',
                      left: -8,
                      borderRadius: '0.375rem',
                      boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.1)',
                      border: '1px solid #ddd',
                      backgroundColor: '#fff',
                      width: '200px',
                    }}
                    containerStyle={{
                      position: 'relative',
                    }}
                    searchStyle={{
                      margin: '0',
                      width: '85%',
                      height: '30px',
                      textAlign: 'left',
                    }}
                    disableCountryCode={false}
                    countryCodeEditable={false}
                  />
                </div>
                {allErrors['personal.phone'] && (
                  <p className="text-red-500 text-sm mt-1 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4" />
                    {allErrors['personal.phone']}
                  </p>
                )}
                <p className="text-xs text-gray-500 mt-1">
                  *Enter your number without a leading 0 after the country code.
                </p>
              </div>
         
              {/* First Name */}
              <div>
                <label className="block text-sm font-medium mb-1">First Name *</label>
                <input
                  type="text"
                  value={formData.personalDetails.firstName}
                  onChange={(e) => updateField('personalDetails.firstName', e.target.value)}
                  className={`w-full p-3 border-2 rounded-lg transition-colors ${
                    allErrors['personal.firstName'] ? 'border-red-500 bg-red-50' : 'border-gray-300 focus:border-blue-500'
                  }`}
                  placeholder="First name"
                />
                {allErrors['personal.firstName'] && (
                  <p className="text-red-500 text-sm mt-1 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4" />
                    {allErrors['personal.firstName']}
                  </p>
                )}
              </div>
        
              {/* Middle Name(s) */}
              <div>
                <label className="block text-sm font-medium mb-1">Middle Name(s)</label>
                <input
                  type="text"
                  value={formData.personalDetails.middleName}
                  onChange={(e) => updateField('personalDetails.middleName', e.target.value)}
                  className="w-full p-3 border-2 border-gray-300 rounded-lg focus:border-blue-500 transition-colors"
                  placeholder="Optional"
                />
                <p className="text-xs text-gray-500 mt-1">If you don't have any middle names you can leave this blank.</p>
              </div>
        
              {/* Surname */}
              <div>
                <label className="block text-sm font-medium mb-1">Surname (Family Name) *</label>
                <input
                  type="text"
                  value={formData.personalDetails.lastName}
                  onChange={(e) => updateField('personalDetails.lastName', e.target.value)}
                  className={`w-full p-3 border-2 rounded-lg transition-colors ${
                    allErrors['personal.lastName'] ? 'border-red-500 bg-red-50' : 'border-gray-300 focus:border-blue-500'
                  }`}
                  placeholder="Last name"
                />
                {allErrors['personal.lastName'] && (
                  <p className="text-red-500 text-sm mt-1 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4" />
                    {allErrors['personal.lastName']}
                  </p>
                )}
              </div>
        
              {/* Checkboxes */}
              <div className="space-y-4 ml-2">
                <label className="block text-sm font-medium mb-1">Other preferences</label>
                
                {/* Previous Name Checkbox and Field */}
                <div className="space-y-2">
                  <div className="flex items-center space-x-3">
                    <input
                      type="checkbox"
                      checked={formData.personalDetails.changedName}
                      onChange={(e) => updateField('personalDetails.changedName', e.target.checked)}
                      className="w-4 h-4 rounded border-2 transition-colors focus:ring-2 focus:ring-offset-2"
                      style={{
                        borderColor: formData.personalDetails.changedName ? (themes?.color1 || '#1E5188') : '#d1d5db',
                        backgroundColor: formData.personalDetails.changedName ? (themes?.color1 || '#1E5188') : 'transparent',
                        accentColor: themes?.color1 || '#1E5188'
                      }}
                    />
                    <span>I have previously changed my name</span>
                  </div>
                  
                  {/* Conditional Previous Names Field */}
                  {formData.personalDetails.changedName && (
                    <div className="ml-7 mt-2">
                      <label className="block text-sm font-medium mb-1">Previous name(s) *</label>
                      <input
                        type="text"
                        value={formData.personalDetails.previousNames}
                        onChange={(e) => updateField('personalDetails.previousNames', e.target.value)}
                        className={`w-full p-3 border-2 rounded-lg transition-colors ${
                          allErrors['personal.previousNames'] ? 'border-red-500 bg-red-50' : 'border-gray-300 focus:border-blue-500'
                        }`}
                        placeholder="Enter your previous name(s)"
                      />
                      {allErrors['personal.previousNames'] && (
                        <p className="text-red-500 text-sm mt-1 flex items-center gap-2">
                          <AlertTriangle className="w-4 h-4" />
                          {allErrors['personal.previousNames']}
                        </p>
                      )}
                      <p className="text-xs text-gray-500 mt-1">Please provide all previous names you have used.</p>
                    </div>
                  )}
                </div>

                {/* Preferred Name Checkbox and Field */}
                <div className="space-y-2">
                  <div className="flex items-center space-x-3">
                    <input
                      type="checkbox"
                      checked={formData.personalDetails.preferredDifferentName}
                      onChange={(e) => updateField('personalDetails.preferredDifferentName', e.target.checked)}
                      className="w-4 h-4 rounded border-2 transition-colors focus:ring-2 focus:ring-offset-2"
                      style={{
                        borderColor: formData.personalDetails.preferredDifferentName ? (themes?.color1 || '#1E5188') : '#d1d5db',
                        backgroundColor: formData.personalDetails.preferredDifferentName ? (themes?.color1 || '#1E5188') : 'transparent',
                        accentColor: themes?.color1 || '#1E5188'
                      }}
                    />
                    <span>I prefer to be called a different name</span>
                  </div>
                  
                  {/* Conditional Preferred Name Field */}
                  {formData.personalDetails.preferredDifferentName && (
                    <div className="ml-7 mt-2">
                      <label className="block text-sm font-medium mb-1">Preferred name *</label>
                      <input
                        type="text"
                        value={formData.personalDetails.preferredName}
                        onChange={(e) => updateField('personalDetails.preferredName', e.target.value)}
                        className={`w-full p-3 border-2 rounded-lg transition-colors ${
                          allErrors['personal.preferredName'] ? 'border-red-500 bg-red-50' : 'border-gray-300 focus:border-blue-500'
                        }`}
                        placeholder="Your nickname - this is the name we will refer to you with"
                      />
                      {allErrors['personal.preferredName'] && (
                        <p className="text-red-500 text-sm mt-1 flex items-center gap-2">
                          <AlertTriangle className="w-4 h-4" />
                          {allErrors['personal.preferredName']}
                        </p>
                      )}
                      <p className="text-xs text-gray-500 mt-1">Your nickname - this is the name we will refer to you with</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        

            case 3:
        return (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <h3 className="text-xl font-bold">Additional Personal Details</h3>
              <p className="text-gray-600 mt-2">We need some additional information to complete your company registration.</p>
            </div>

            {/* Date of Birth */}
            <div>
              <label className="block text-sm font-medium mb-1">What is your date of birth? *</label>
              <p className="text-xs text-gray-500 mb-2">You must be over 18 years old to create a company with us</p>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs font-medium mb-1">Day *</label>
                  <select
                    value={formData.personalDetails.dateOfBirth.day}
                    onChange={(e) => updateField('personalDetails.dateOfBirth.day', e.target.value)}
                    className={`w-full p-3 border-2 rounded-lg transition-colors ${
                      allErrors['personal.dateOfBirth.day'] ? 'border-red-500 bg-red-50' : 'border-gray-300 focus:border-blue-500'
                    }`}
                  >
                    <option value="">Day</option>
                    {Array.from({ length: 31 }, (_, i) => i + 1).map(day => (
                      <option key={day} value={day.toString().padStart(2, '0')}>
                        {day}
                      </option>
                    ))}
                  </select>
                  {allErrors['personal.dateOfBirth.day'] && (
                    <p className="text-red-500 text-sm mt-1 flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4" />
                      {allErrors['personal.dateOfBirth.day']}
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1">Month *</label>
                  <select
                    value={formData.personalDetails.dateOfBirth.month}
                    onChange={(e) => updateField('personalDetails.dateOfBirth.month', e.target.value)}
                    className={`w-full p-3 border-2 rounded-lg transition-colors ${
                      allErrors['personal.dateOfBirth.month'] ? 'border-red-500 bg-red-50' : 'border-gray-300 focus:border-blue-500'
                    }`}
                  >
                    <option value="">Month</option>
                    {[
                      'January', 'February', 'March', 'April', 'May', 'June',
                      'July', 'August', 'September', 'October', 'November', 'December'
                    ].map((month, index) => (
                      <option key={month} value={(index + 1).toString().padStart(2, '0')}>
                        {month}
                      </option>
                    ))}
                  </select>
                  {allErrors['personal.dateOfBirth.month'] && (
                    <p className="text-red-500 text-sm mt-1 flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4" />
                      {allErrors['personal.dateOfBirth.month']}
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1">Year *</label>
                  <select
                    value={formData.personalDetails.dateOfBirth.year}
                    onChange={(e) => updateField('personalDetails.dateOfBirth.year', e.target.value)}
                    className={`w-full p-3 border-2 rounded-lg transition-colors ${
                      allErrors['personal.dateOfBirth.year'] ? 'border-red-500 bg-red-50' : 'border-gray-300 focus:border-blue-500'
                    }`}
                  >
                    <option value="">Year</option>
                    {Array.from({ length: 83 }, (_, i) => new Date().getFullYear() - i).map(year => (
                      <option key={year} value={year.toString()}>
                        {year}
                      </option>
                    ))}
                  </select>
                  {allErrors['personal.dateOfBirth.year'] && (
                    <p className="text-red-500 text-sm mt-1 flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4" />
                      {allErrors['personal.dateOfBirth.year']}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Nationality */}
            <div>
              <label className="block text-sm font-medium mb-1">Nationality *</label>
              <select
                value={formData.personalDetails.nationality}
                onChange={(e) => updateField('personalDetails.nationality', e.target.value)}
                className={`w-full p-3 border-2 rounded-lg transition-colors ${
                  allErrors['personal.nationality'] ? 'border-red-500 bg-red-50' : 'border-gray-300 focus:border-blue-500'
                }`}
              >
                <option value="">Select nationality</option>
                <option value="British">British, UK - United Kingdom of Great Britain and Northern Ireland</option>
                <option value="Irish">Irish</option>
                <option value="Scottish">Scottish</option>
                <option value="Welsh">Welsh</option>
                <option value="Northern Irish">Northern Irish</option>
                <option value="Other">Other</option>
              </select>
              {allErrors['personal.nationality'] && (
                <p className="text-red-500 text-sm mt-1 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4" />
                  {allErrors['personal.nationality']}
                </p>
              )}
            </div>

            {/* City of Birth */}
            <div>
              <label className="block text-sm font-medium mb-1">City of birth *</label>
              <p className="text-xs text-gray-500 mb-2">This is required to incorporate your company.</p>
              <input
                type="text"
                value={formData.personalDetails.cityOfBirth}
                onChange={(e) => updateField('personalDetails.cityOfBirth', e.target.value)}
                className={`w-full p-3 border-2 rounded-lg transition-colors ${
                  allErrors['personal.cityOfBirth'] ? 'border-red-500 bg-red-50' : 'border-gray-300 focus:border-blue-500'
                }`}
                placeholder="Enter your city of birth"
              />
              {allErrors['personal.cityOfBirth'] && (
                <p className="text-red-500 text-sm mt-1 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4" />
                  {allErrors['personal.cityOfBirth']}
                </p>
              )}
            </div>

            {/* Passport Number */}
            <div>
              <label className="block text-sm font-medium mb-1">Passport number *</label>
              <input
                type="text"
                value={formData.personalDetails.passportNumber}
                onChange={(e) => updateField('personalDetails.passportNumber', e.target.value)}
                className={`w-full p-3 border-2 rounded-lg transition-colors ${
                  allErrors['personal.passportNumber'] ? 'border-red-500 bg-red-50' : 'border-gray-300 focus:border-blue-500'
                }`}
                placeholder="Enter your passport number"
              />
              {allErrors['personal.passportNumber'] && (
                <p className="text-red-500 text-sm mt-1 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4" />
                  {allErrors['personal.passportNumber']}
                </p>
              )}
            </div>

            {/* Employment Status */}
            <div>
              <label className="block text-sm font-medium mb-1">Employment status *</label>
              <select
                value={formData.personalDetails.employmentStatus}
                onChange={(e) => updateField('personalDetails.employmentStatus', e.target.value)}
                className={`w-full p-3 border-2 rounded-lg transition-colors ${
                  allErrors['personal.employmentStatus'] ? 'border-red-500 bg-red-50' : 'border-gray-300 focus:border-blue-500'
                }`}
              >
                <option value="">Select employment status</option>
                <option value="Employed Full Time">Employed Full Time</option>
                <option value="Employed Part Time">Employed Part Time</option>
                <option value="Self-Employed">Self-Employed</option>
                <option value="Unemployed">Unemployed</option>
                <option value="Pensioner">Pensioner</option>
                <option value="Student">Student</option>
                <option value="Other">Other</option>
              </select>
              {allErrors['personal.employmentStatus'] && (
                <p className="text-red-500 text-sm mt-1 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4" />
                  {allErrors['personal.employmentStatus']}
                </p>
              )}
            </div>

            {/* Employment Status Other */}
            {formData.personalDetails.employmentStatus === 'Other' && (
              <div>
                <label className="block text-sm font-medium mb-1">Please explain your employment status *</label>
                <input
                  type="text"
                  value={formData.personalDetails.employmentStatusOther}
                  onChange={(e) => updateField('personalDetails.employmentStatusOther', e.target.value)}
                  className={`w-full p-3 border-2 rounded-lg transition-colors ${
                    allErrors['personal.employmentStatusOther'] ? 'border-red-500 bg-red-50' : 'border-gray-300 focus:border-blue-500'
                  }`}
                  placeholder="Please explain your employment status"
                />
                {allErrors['personal.employmentStatusOther'] && (
                  <p className="text-red-500 text-sm mt-1 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4" />
                    {allErrors['personal.employmentStatusOther']}
                  </p>
                )}
              </div>
            )}

            {/* Profession */}
            <div>
              <label className="block text-sm font-medium mb-1">Profession *</label>
              <input
                type="text"
                value={formData.personalDetails.profession}
                onChange={(e) => updateField('personalDetails.profession', e.target.value)}
                className={`w-full p-3 border-2 rounded-lg transition-colors ${
                  allErrors['personal.profession'] ? 'border-red-500 bg-red-50' : 'border-gray-300 focus:border-blue-500'
                }`}
                placeholder={
                  ['Unemployed', 'Pensioner', 'Student'].includes(formData.personalDetails.employmentStatus) 
                    ? formData.personalDetails.employmentStatus 
                    : "Enter your profession"
                }
                disabled={['Unemployed', 'Pensioner', 'Student'].includes(formData.personalDetails.employmentStatus)}
              />
              {allErrors['personal.profession'] && (
                <p className="text-red-500 text-sm mt-1 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4" />
                  {allErrors['personal.profession']}
                </p>
              )}
            </div>

            {/* First Time Landlord */}
            <div>
              <label className="block text-sm font-medium mb-1">Are you a first time landlord? *</label>
              <p className="text-xs text-gray-500 mb-2">This question is asked to better understand our customer demographics.</p>
              <select
                value={formData.personalDetails.isFirstTimeLandlord}
                onChange={(e) => updateField('personalDetails.isFirstTimeLandlord', e.target.value)}
                className={`w-full p-3 border-2 rounded-lg transition-colors ${
                  allErrors['personal.isFirstTimeLandlord'] ? 'border-red-500 bg-red-50' : 'border-gray-300 focus:border-blue-500'
                }`}
              >
                <option value="">Select answer</option>
                <option value="yes">Yes</option>
                <option value="no">No</option>
              </select>
              {allErrors['personal.isFirstTimeLandlord'] && (
                <p className="text-red-500 text-sm mt-1 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4" />
                  {allErrors['personal.isFirstTimeLandlord']}
                </p>
              )}
            </div>

            {/* First Time Buyer - Conditional */}
            {formData.personalDetails.isFirstTimeLandlord === 'yes' && (
              <div>
                <label className="block text-sm font-medium mb-1">Are you a first time buyer? *</label>
                <p className="text-xs text-gray-500 mb-2">If you have ever owned a residential property, whether owner-occupied or as a buy-to-let, whether in the UK or elsewhere.</p>
                <select
                  value={formData.personalDetails.isFirstTimeBuyer}
                  onChange={(e) => updateField('personalDetails.isFirstTimeBuyer', e.target.value)}
                  className={`w-full p-3 border-2 rounded-lg transition-colors ${
                    allErrors['personal.isFirstTimeBuyer'] ? 'border-red-500 bg-red-50' : 'border-gray-300 focus:border-blue-500'
                  }`}
                >
                  <option value="">Select answer</option>
                  <option value="yes">Yes</option>
                  <option value="no">No</option>
                </select>
                {allErrors['personal.isFirstTimeBuyer'] && (
                  <p className="text-red-500 text-sm mt-1 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4" />
                    {allErrors['personal.isFirstTimeBuyer']}
                  </p>
                )}
              </div>
            )}

            {/* Property Ownership - Conditional */}
            {formData.personalDetails.isFirstTimeLandlord === 'no' && (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1">How many buy-to-let properties do you own globally? *</label>
                  <div className="flex items-center justify-center space-x-2">
                    <button
                      type="button"
                      onClick={() => {
                        const current = parseInt(formData.personalDetails.globalPropertiesOwned) || 0;
                        if (current > 0) {
                          updateField('personalDetails.globalPropertiesOwned', (current - 1).toString());
                        }
                      }}
                      className="w-10 h-10 rounded-full border-2 border-gray-300 flex items-center justify-center hover:border-blue-500 transition-colors"
                      disabled={parseInt(formData.personalDetails.globalPropertiesOwned) <= 0}
                    >
                      <span className="text-lg font-bold">-</span>
                    </button>
                    <input
                      type="number"
                      value={formData.personalDetails.globalPropertiesOwned}
                      onChange={(e) => updateField('personalDetails.globalPropertiesOwned', e.target.value)}
                      className={`w-20 text-center p-3 border-2 rounded-lg transition-colors ${
                        allErrors['personal.globalPropertiesOwned'] ? 'border-red-500 bg-red-50' : 'border-gray-300 focus:border-blue-500'
                      }`}
                      min="0"
                      max="999"
                    />
                    <button
                      type="button"
                      onClick={() => {
                        const current = parseInt(formData.personalDetails.globalPropertiesOwned) || 0;
                        updateField('personalDetails.globalPropertiesOwned', (current + 1).toString());
                      }}
                      className="w-10 h-10 rounded-full border-2 border-gray-300 flex items-center justify-center hover:border-blue-500 transition-colors"
                    >
                      <span className="text-lg font-bold">+</span>
                    </button>
                  </div>
                  {allErrors['personal.globalPropertiesOwned'] && (
                    <p className="text-red-500 text-sm mt-1 flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4" />
                      {allErrors['personal.globalPropertiesOwned']}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">How many of these are in the UK? *</label>
                  <div className="flex items-center justify-center space-x-2">
                    <button
                      type="button"
                      onClick={() => {
                        const current = parseInt(formData.personalDetails.ukPropertiesOwned) || 0;
                        if (current > 0) {
                          updateField('personalDetails.ukPropertiesOwned', (current - 1).toString());
                        }
                      }}
                      className="w-10 h-10 rounded-full border-2 border-gray-300 flex items-center justify-center hover:border-blue-500 transition-colors"
                      disabled={parseInt(formData.personalDetails.ukPropertiesOwned) <= 0}
                    >
                      <span className="text-lg font-bold">-</span>
                    </button>
                    <input
                      type="number"
                      value={formData.personalDetails.ukPropertiesOwned}
                      onChange={(e) => updateField('personalDetails.ukPropertiesOwned', e.target.value)}
                      className={`w-20 text-center p-3 border-2 rounded-lg transition-colors ${
                        allErrors['personal.ukPropertiesOwned'] ? 'border-red-500 bg-red-50' : 'border-gray-300 focus:border-blue-500'
                      }`}
                      min="0"
                      max={parseInt(formData.personalDetails.globalPropertiesOwned) || 0}
                    />
                    <button
                      type="button"
                      onClick={() => {
                        const current = parseInt(formData.personalDetails.ukPropertiesOwned) || 0;
                        const globalTotal = parseInt(formData.personalDetails.globalPropertiesOwned) || 0;
                        if (current < globalTotal) {
                          updateField('personalDetails.ukPropertiesOwned', (current + 1).toString());
                        }
                      }}
                      className="w-10 h-10 rounded-full border-2 border-gray-300 flex items-center justify-center hover:border-blue-500 transition-colors"
                      disabled={parseInt(formData.personalDetails.ukPropertiesOwned) >= parseInt(formData.personalDetails.globalPropertiesOwned)}
                    >
                      <span className="text-lg font-bold">+</span>
                    </button>
                  </div>
                  {allErrors['personal.ukPropertiesOwned'] && (
                    <p className="text-red-500 text-sm mt-1 flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4" />
                      {allErrors['personal.ukPropertiesOwned']}
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>
        );

              case 4:
        return (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <h3 className="text-xl font-bold">Registered Address</h3>
              <p className="text-gray-600 mt-2">Where will your company be officially registered?</p>
            </div>
            
            {/* Country of Residence */}
            <div>
              <label className="block text-sm font-medium mb-1">Country of residence *</label>
              <select
                value={formData.registeredAddress.country}
                onChange={(e) => updateField('registeredAddress.country', e.target.value)}
                className={`w-full p-3 border-2 rounded-lg transition-colors ${
                  allErrors['address.country'] ? 'border-red-500 bg-red-50' : 'border-gray-300 focus:border-blue-500'
                }`}
              >
                <option value="">Select country</option>
                <option value="United Kingdom of Great Britain and Northern Ireland">United Kingdom of Great Britain and Northern Ireland</option>
                <option value="Afghanistan">Afghanistan</option>
                <option value="Albania">Albania</option>
                <option value="Algeria">Algeria</option>
                <option value="Andorra">Andorra</option>
                <option value="Angola">Angola</option>
                <option value="Antigua and Barbuda">Antigua and Barbuda</option>
                <option value="Argentina">Argentina</option>
                <option value="Armenia">Armenia</option>
                <option value="Australia">Australia</option>
                <option value="Austria">Austria</option>
                <option value="Azerbaijan">Azerbaijan</option>
                <option value="Bahamas">Bahamas</option>
                <option value="Bahrain">Bahrain</option>
                <option value="Bangladesh">Bangladesh</option>
                <option value="Barbados">Barbados</option>
                <option value="Belarus">Belarus</option>
                <option value="Belgium">Belgium</option>
                <option value="Belize">Belize</option>
                <option value="Benin">Benin</option>
                <option value="Bhutan">Bhutan</option>
                <option value="Bolivia">Bolivia</option>
                <option value="Bosnia and Herzegovina">Bosnia and Herzegovina</option>
                <option value="Botswana">Botswana</option>
                <option value="Brazil">Brazil</option>
                <option value="Brunei">Brunei</option>
                <option value="Bulgaria">Bulgaria</option>
                <option value="Burkina Faso">Burkina Faso</option>
                <option value="Burundi">Burundi</option>
                <option value="Cambodia">Cambodia</option>
                <option value="Cameroon">Cameroon</option>
                <option value="Canada">Canada</option>
                <option value="Cape Verde">Cape Verde</option>
                <option value="Central African Republic">Central African Republic</option>
                <option value="Chad">Chad</option>
                <option value="Chile">Chile</option>
                <option value="China">China</option>
                <option value="Colombia">Colombia</option>
                <option value="Comoros">Comoros</option>
                <option value="Congo">Congo</option>
                <option value="Costa Rica">Costa Rica</option>
                <option value="Croatia">Croatia</option>
                <option value="Cuba">Cuba</option>
                <option value="Cyprus">Cyprus</option>
                <option value="Czech Republic">Czech Republic</option>
                <option value="Democratic Republic of the Congo">Democratic Republic of the Congo</option>
                <option value="Denmark">Denmark</option>
                <option value="Djibouti">Djibouti</option>
                <option value="Dominica">Dominica</option>
                <option value="Dominican Republic">Dominican Republic</option>
                <option value="East Timor">East Timor</option>
                <option value="Ecuador">Ecuador</option>
                <option value="Egypt">Egypt</option>
                <option value="El Salvador">El Salvador</option>
                <option value="Equatorial Guinea">Equatorial Guinea</option>
                <option value="Eritrea">Eritrea</option>
                <option value="Estonia">Estonia</option>
                <option value="Eswatini">Eswatini</option>
                <option value="Ethiopia">Ethiopia</option>
                <option value="Fiji">Fiji</option>
                <option value="Finland">Finland</option>
                <option value="France">France</option>
                <option value="Gabon">Gabon</option>
                <option value="Gambia">Gambia</option>
                <option value="Georgia">Georgia</option>
                <option value="Germany">Germany</option>
                <option value="Ghana">Ghana</option>
                <option value="Greece">Greece</option>
                <option value="Grenada">Grenada</option>
                <option value="Guatemala">Guatemala</option>
                <option value="Guinea">Guinea</option>
                <option value="Guinea-Bissau">Guinea-Bissau</option>
                <option value="Guyana">Guyana</option>
                <option value="Haiti">Haiti</option>
                <option value="Honduras">Honduras</option>
                <option value="Hungary">Hungary</option>
                <option value="Iceland">Iceland</option>
                <option value="India">India</option>
                <option value="Indonesia">Indonesia</option>
                <option value="Iran">Iran</option>
                <option value="Iraq">Iraq</option>
                <option value="Ireland">Ireland</option>
                <option value="Israel">Israel</option>
                <option value="Italy">Italy</option>
                <option value="Ivory Coast">Ivory Coast</option>
                <option value="Jamaica">Jamaica</option>
                <option value="Japan">Japan</option>
                <option value="Jordan">Jordan</option>
                <option value="Kazakhstan">Kazakhstan</option>
                <option value="Kenya">Kenya</option>
                <option value="Kiribati">Kiribati</option>
                <option value="Kuwait">Kuwait</option>
                <option value="Kyrgyzstan">Kyrgyzstan</option>
                <option value="Laos">Laos</option>
                <option value="Latvia">Latvia</option>
                <option value="Lebanon">Lebanon</option>
                <option value="Lesotho">Lesotho</option>
                <option value="Liberia">Liberia</option>
                <option value="Libya">Libya</option>
                <option value="Liechtenstein">Liechtenstein</option>
                <option value="Lithuania">Lithuania</option>
                <option value="Luxembourg">Luxembourg</option>
                <option value="Madagascar">Madagascar</option>
                <option value="Malawi">Malawi</option>
                <option value="Malaysia">Malaysia</option>
                <option value="Maldives">Maldives</option>
                <option value="Mali">Mali</option>
                <option value="Malta">Malta</option>
                <option value="Marshall Islands">Marshall Islands</option>
                <option value="Mauritania">Mauritania</option>
                <option value="Mauritius">Mauritius</option>
                <option value="Mexico">Mexico</option>
                <option value="Micronesia">Micronesia</option>
                <option value="Moldova">Moldova</option>
                <option value="Monaco">Monaco</option>
                <option value="Mongolia">Mongolia</option>
                <option value="Montenegro">Montenegro</option>
                <option value="Morocco">Morocco</option>
                <option value="Mozambique">Mozambique</option>
                <option value="Myanmar">Myanmar</option>
                <option value="Namibia">Namibia</option>
                <option value="Nauru">Nauru</option>
                <option value="Nepal">Nepal</option>
                <option value="Netherlands">Netherlands</option>
                <option value="New Zealand">New Zealand</option>
                <option value="Nicaragua">Nicaragua</option>
                <option value="Niger">Niger</option>
                <option value="Nigeria">Nigeria</option>
                <option value="North Korea">North Korea</option>
                <option value="North Macedonia">North Macedonia</option>
                <option value="Norway">Norway</option>
                <option value="Oman">Oman</option>
                <option value="Pakistan">Pakistan</option>
                <option value="Palau">Palau</option>
                <option value="Panama">Panama</option>
                <option value="Papua New Guinea">Papua New Guinea</option>
                <option value="Paraguay">Paraguay</option>
                <option value="Peru">Peru</option>
                <option value="Philippines">Philippines</option>
                <option value="Poland">Poland</option>
                <option value="Portugal">Portugal</option>
                <option value="Qatar">Qatar</option>
                <option value="Romania">Romania</option>
                <option value="Russia">Russia</option>
                <option value="Rwanda">Rwanda</option>
                <option value="Saint Kitts and Nevis">Saint Kitts and Nevis</option>
                <option value="Saint Lucia">Saint Lucia</option>
                <option value="Saint Vincent and the Grenadines">Saint Vincent and the Grenadines</option>
                <option value="Samoa">Samoa</option>
                <option value="San Marino">San Marino</option>
                <option value="Sao Tome and Principe">Sao Tome and Principe</option>
                <option value="Saudi Arabia">Saudi Arabia</option>
                <option value="Senegal">Senegal</option>
                <option value="Serbia">Serbia</option>
                <option value="Seychelles">Seychelles</option>
                <option value="Sierra Leone">Sierra Leone</option>
                <option value="Singapore">Singapore</option>
                <option value="Slovakia">Slovakia</option>
                <option value="Slovenia">Slovenia</option>
                <option value="Solomon Islands">Solomon Islands</option>
                <option value="Somalia">Somalia</option>
                <option value="South Africa">South Africa</option>
                <option value="South Korea">South Korea</option>
                <option value="South Sudan">South Sudan</option>
                <option value="Spain">Spain</option>
                <option value="Sri Lanka">Sri Lanka</option>
                <option value="Sudan">Sudan</option>
                <option value="Suriname">Suriname</option>
                <option value="Sweden">Sweden</option>
                <option value="Switzerland">Switzerland</option>
                <option value="Syria">Syria</option>
                <option value="Taiwan">Taiwan</option>
                <option value="Tajikistan">Tajikistan</option>
                <option value="Tanzania">Tanzania</option>
                <option value="Thailand">Thailand</option>
                <option value="Togo">Togo</option>
                <option value="Tonga">Tonga</option>
                <option value="Trinidad and Tobago">Trinidad and Tobago</option>
                <option value="Tunisia">Tunisia</option>
                <option value="Turkey">Turkey</option>
                <option value="Turkmenistan">Turkmenistan</option>
                <option value="Tuvalu">Tuvalu</option>
                <option value="Uganda">Uganda</option>
                <option value="Ukraine">Ukraine</option>
                <option value="United Arab Emirates">United Arab Emirates</option>
                <option value="United States">United States</option>
                <option value="Uruguay">Uruguay</option>
                <option value="Uzbekistan">Uzbekistan</option>
                <option value="Vanuatu">Vanuatu</option>
                <option value="Vatican City">Vatican City</option>
                <option value="Venezuela">Venezuela</option>
                <option value="Vietnam">Vietnam</option>
                <option value="Yemen">Yemen</option>
                <option value="Zambia">Zambia</option>
                <option value="Zimbabwe">Zimbabwe</option>
              </select>
              {allErrors['address.country'] && (
                <p className="text-red-500 text-sm mt-1 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4" />
                  {allErrors['address.country']}
                </p>
              )}
            </div>

            {/* Flat number / Building number / House name */}
            <div>
              <label className="block text-sm font-medium mb-1">Flat number / Building number / House name *</label>
              <p className="text-xs text-gray-500 mb-2">This can also be a floor number, street number, house number, or a combination of any of these</p>
              <input
                type="text"
                value={formData.registeredAddress.flatNumber}
                onChange={(e) => updateField('registeredAddress.flatNumber', e.target.value)}
                className={`w-full p-3 border-2 rounded-lg transition-colors ${
                  allErrors['address.flatNumber'] ? 'border-red-500 bg-red-50' : 'border-gray-300 focus:border-blue-500'
                }`}
                placeholder="e.g., Flat 3, 42B, Rose Cottage"
                disabled={isSubmitting}
              />
              {allErrors['address.flatNumber'] && (
                <p className="text-red-500 text-sm mt-1 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4" />
                  {allErrors['address.flatNumber']}
                </p>
              )}
            </div>

            {/* Address Line 1 */}
            <div>
              <label className="block text-sm font-medium mb-1">Address line 1 *</label>
              <input
                type="text"
                value={formData.registeredAddress.line1}
                onChange={(e) => updateField('registeredAddress.line1', e.target.value)}
                className={`w-full p-3 border-2 rounded-lg transition-colors ${
                  allErrors['address.line1'] ? 'border-red-500 bg-red-50' : 'border-gray-300 focus:border-blue-500'
                }`}
                placeholder="Street address"
                disabled={isSubmitting}
              />
              {allErrors['address.line1'] && (
                <p className="text-red-500 text-sm mt-1 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4" />
                  {allErrors['address.line1']}
                </p>
              )}
            </div>

            {/* Address Line 2 */}
            <div>
              <label className="block text-sm font-medium mb-1">Address line 2</label>
              <input
                type="text"
                value={formData.registeredAddress.line2}
                onChange={(e) => updateField('registeredAddress.line2', e.target.value)}
                className="w-full p-3 border-2 border-gray-300 rounded-lg focus:border-blue-500 transition-colors"
                placeholder="Apartment, suite, etc. (optional)"
                disabled={isSubmitting}
              />
            </div>

            {/* City and Postcode */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">City *</label>
                <input
                  type="text"
                  value={formData.registeredAddress.city}
                  onChange={(e) => updateField('registeredAddress.city', e.target.value)}
                  className={`w-full p-3 border-2 rounded-lg transition-colors ${
                    allErrors['address.city'] ? 'border-red-500 bg-red-50' : 'border-gray-300 focus:border-blue-500'
                  }`}
                  placeholder="City"
                  disabled={isSubmitting}
                />
                {allErrors['address.city'] && (
                  <p className="text-red-500 text-sm mt-1 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4" />
                    {allErrors['address.city']}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Postcode *</label>
                <input
                  type="text"
                  value={formData.registeredAddress.postcode}
                  onChange={(e) => updateField('registeredAddress.postcode', e.target.value.toUpperCase())}
                  className={`w-full p-3 border-2 rounded-lg transition-colors ${
                    allErrors['address.postcode'] ? 'border-red-500 bg-red-50' : 'border-gray-300 focus:border-blue-500'
                  }`}
                  placeholder="SW1A 1AA"
                  disabled={isSubmitting}
                />
                {allErrors['address.postcode'] && (
                  <p className="text-red-500 text-sm mt-1 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4" />
                    {allErrors['address.postcode']}
                  </p>
                )}
              </div>
            </div>
          </div>
        );

        case 5:
        return (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <h3 className="text-xl font-bold">Review your answers</h3>
              <p className="text-gray-600 mt-2">Please review your information before proceeding</p>
            </div>

            {/* Choose your company structure Section */}
            <div className="bg-gray-50 p-6 rounded-lg">
              <h4 className="font-semibold text-lg mb-4 flex items-center gap-2">
                <FontAwesomeIcon icon={faBuilding} className="w-5 h-5 text-purple-600" />
                Choose your company structure
              </h4>
              
              <div>
                <label className="text-sm font-medium text-gray-600">Company type</label>
                <p className="text-base capitalize">{formData.companyType}</p>
              </div>

              <div className="mt-4 flex justify-end">
                <button
                  type="button"
                  onClick={() => {
                    setCurrentStep(1);
                    // Scroll to top of the form container
                    const formContainer = document.querySelector('.flex-1.overflow-y-auto');
                    if (formContainer) {
                      formContainer.scrollTo({ top: 0, behavior: 'smooth' });
                    }
                  }}
                  className="px-4 py-2 text-sm bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors flex items-center gap-2"
                >
                  <Edit className="w-4 h-4" />
                  Edit Company Structure
                </button>
              </div>
            </div>

            {/* Your Personal Details Section */}
            <div className="bg-gray-50 p-6 rounded-lg">
              <h4 className="font-semibold text-lg mb-4 flex items-center gap-2">
                <User className="w-5 h-5 text-blue-600" />
                Your Personal Details
              </h4>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-600">Email</label>
                  <p className="text-base">{formData.personalDetails.email}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-600">Phone number</label>
                  <p className="text-base">{formData.personalDetails.phone}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-600">First Name</label>
                  <p className="text-base">{formData.personalDetails.firstName}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-600">Middle Name(s)</label>
                  <p className="text-base">{formData.personalDetails.middleName || 'Not provided'}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-600">Surname</label>
                  <p className="text-base">{formData.personalDetails.lastName}</p>
                </div>
                {formData.personalDetails.changedName && (
                  <div>
                    <label className="text-sm font-medium text-gray-600">Previous name(s)</label>
                    <p className="text-base">{formData.personalDetails.previousNames}</p>
                  </div>
                )}
                {formData.personalDetails.preferredDifferentName && (
                  <div>
                    <label className="text-sm font-medium text-gray-600">Preferred name</label>
                    <p className="text-base">{formData.personalDetails.preferredName}</p>
                  </div>
                )}
              </div>

              <div className="mt-4 flex justify-end">
                <button
                  type="button"
                  onClick={() => {
                    setCurrentStep(2);
                    // Scroll to top of the form container
                    const formContainer = document.querySelector('.flex-1.overflow-y-auto');
                    if (formContainer) {
                      formContainer.scrollTo({ top: 0, behavior: 'smooth' });
                    }
                  }}
                  className="px-4 py-2 text-sm bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors flex items-center gap-2"
                >
                  <Edit className="w-4 h-4" />
                  Edit Personal Details
                </button>
              </div>
            </div>

            {/* Additional Personal Details Section */}
            <div className="bg-gray-50 p-6 rounded-lg">
              <h4 className="font-semibold text-lg mb-4 flex items-center gap-2">
                <User className="w-5 h-5 text-green-600" />
                Additional Personal Details
              </h4>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-600">Date of birth</label>
                  <p className="text-base">
                    {formData.personalDetails.dateOfBirth.day} {formData.personalDetails.dateOfBirth.month} {formData.personalDetails.dateOfBirth.year}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-600">Nationality</label>
                  <p className="text-base">{formData.personalDetails.nationality}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-600">City of birth</label>
                  <p className="text-base">{formData.personalDetails.cityOfBirth}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-600">Passport number</label>
                  <p className="text-base">{formData.personalDetails.passportNumber}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-600">Employment status</label>
                  <p className="text-base">{formData.personalDetails.employmentStatus}</p>
                </div>
                {formData.personalDetails.employmentStatusOther && (
                  <div>
                    <label className="text-sm font-medium text-gray-600">Employment status explanation</label>
                    <p className="text-base">{formData.personalDetails.employmentStatusOther}</p>
                  </div>
                )}
                <div>
                  <label className="text-sm font-medium text-gray-600">Profession</label>
                  <p className="text-base">{formData.personalDetails.profession}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-600">First time landlord</label>
                  <p className="text-base">
                    {formData.personalDetails.isFirstTimeLandlord === 'yes' ? 'Yes' : 'No'}
                  </p>
                </div>
                {formData.personalDetails.isFirstTimeLandlord === 'yes' && (
                  <div>
                    <label className="text-sm font-medium text-gray-600">First time buyer</label>
                    <p className="text-base">
                      {formData.personalDetails.isFirstTimeBuyer === 'yes' ? 'Yes' : 'No'}
                    </p>
                  </div>
                )}
                {formData.personalDetails.isFirstTimeLandlord === 'no' && (
                  <>
                    <div>
                      <label className="text-sm font-medium text-gray-600">Global properties owned</label>
                      <p className="text-base">{formData.personalDetails.globalPropertiesOwned}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-600">UK properties owned</label>
                      <p className="text-base">{formData.personalDetails.ukPropertiesOwned}</p>
                    </div>
                  </>
                )}
              </div>

              <div className="mt-4 flex justify-end">
                <button
                  type="button"
                  onClick={() => {
                    setCurrentStep(3);
                    // Scroll to top of the form container
                    const formContainer = document.querySelector('.flex-1.overflow-y-auto');
                    if (formContainer) {
                      formContainer.scrollTo({ top: 0, behavior: 'smooth' });
                    }
                  }}
                  className="px-4 py-2 text-sm bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors flex items-center gap-2"
                >
                  <Edit className="w-4 h-4" />
                  Edit Additional Personal Details
                </button>
              </div>
            </div>

            {/* Registered Address Section */}
            <div className="bg-gray-50 p-6 rounded-lg">
              <h4 className="font-semibold text-lg mb-4 flex items-center gap-2">
                <Home className="w-5 h-5 text-orange-600" />
                Registered Address
              </h4>
              
              <div className="space-y-2">
                <div>
                  <label className="text-sm font-medium text-gray-600">Country</label>
                  <p className="text-base">{formData.registeredAddress.country}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-600">Flat number / Building number / House name</label>
                  <p className="text-base">{formData.registeredAddress.flatNumber}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-600">Address line 1</label>
                  <p className="text-base">{formData.registeredAddress.line1}</p>
                </div>
                {formData.registeredAddress.line2 && (
                  <div>
                    <label className="text-sm font-medium text-gray-600">Address line 2</label>
                    <p className="text-base">{formData.registeredAddress.line2}</p>
                  </div>
                )}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-gray-600">City</label>
                    <p className="text-base">{formData.registeredAddress.city}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-600">Postcode</label>
                    <p className="text-base">{formData.registeredAddress.postcode}</p>
                  </div>
                </div>
              </div>

              <div className="mt-4 flex justify-end">
                <button
                  type="button"
                  onClick={() => {
                    setCurrentStep(4);
                    // Scroll to top of the form container
                    const formContainer = document.querySelector('.flex-1.overflow-y-auto');
                    if (formContainer) {
                      formContainer.scrollTo({ top: 0, behavior: 'smooth' });
                    }
                  }}
                  className="px-4 py-2 text-sm bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors flex items-center gap-2"
                >
                  <Edit className="w-4 h-4" />
                  Edit Address
                </button>
              </div>
            </div>
          </div>
        );





      default:
        return null;
    }
  };

  return (
    <div className="p-6 space-y-6 h-full flex flex-col">
      {/* Progress Indicator */}
      <div className="flex items-center justify-between mb-6">
        {[1, 2, 3, 4, 5].map((step) => (
          <div key={step} className="flex items-center">
            <div
              className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold transition-all ${
                step <= currentStep
                  ? 'shadow-lg'
                  : 'bg-gray-200 text-gray-600'
              }`}
              style={{
                backgroundColor: step <= currentStep ? (themes?.color1 || '#1E5188') : undefined,
                color: step <= currentStep ? (themes?.fontColor || '#ffffff') : undefined
              }}
            >
              {step < currentStep ? '✓' : step}
            </div>
            {step < 5 && (
              <div
                className={`w-16 h-2 mx-2 rounded-full transition-all ${
                  step < currentStep ? 'shadow-sm' : 'bg-gray-200'
                }`}
                style={{
                  backgroundColor: step < currentStep ? (themes?.color1 || '#1E5188') : undefined
                }}
              />
            )}
          </div>
        ))}
      </div>

      {/* Form Content - Scrollable */}
      <div className="flex-1 overflow-y-auto min-h-0">
        {renderStep()}
      </div>

      {/* Navigation Buttons - Fixed at bottom */}
      <div className="flex justify-between pt-6 border-t bg-white">
        <button
          type="button"
          onClick={currentStep === 1 ? onCancel : handleBack}
          disabled={isSubmitting}
          className="px-6 py-3 border-2 border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 transition-colors font-medium"
        >
          {currentStep === 1 ? 'Cancel' : '← Back'}
        </button>

        <button
          type="button"
          onClick={currentStep === 5 ? handleSubmit : handleNext}
          disabled={isSubmitting}
          className="px-6 py-3 rounded-lg disabled:opacity-50 transition-all font-bold shadow-lg hover:shadow-xl transform hover:scale-105"
          style={{ 
            backgroundColor: themes?.color1 || '#1E5188',
            color: themes?.fontColor || '#ffffff'
          }}
          onMouseEnter={(e) => {
            if (!isSubmitting) {
              e.currentTarget.style.backgroundColor = themes?.color2 || themes?.color1 || '#1E5188';
            }
          }}
          onMouseLeave={(e) => {
            if (!isSubmitting) {
              e.currentTarget.style.backgroundColor = themes?.color1 || '#1E5188';
            }
          }}
        >
          {isSubmitting ? (
            <span className="flex items-center gap-2">
              <Loader2 className="animate-spin h-4 w-4" />
              Processing...
            </span>
          ) : currentStep === 5 ? (
            'Confirm and Submit To Our Partner'
          ) : (
            'Next →'
          )}
        </button>
      </div>
    </div>
  );
};

export default CompanySetupForm;